import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import moment from 'moment';
import { catchError, takeUntil, tap, Observable, throwError } from 'rxjs';

import { resetHubspotCookiesAndReloadPage } from '../../../lib/hubspot';
import { AuthService } from '../../auth/auth.service';
import { SnackbarService } from '../services/snackbar.service';
import { UserService } from '../services/user.service';
import { isServerError } from '../utils';

@Injectable()
export class NetworkInterceptor implements HttpInterceptor {
  private userService = inject(UserService);
  private router = inject(Router);
  private authService = inject(AuthService);
  private snackbarService = inject(SnackbarService);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      takeUntil(this.authService.getLogoutSignal()),
      tap((response: HttpEvent<any>) => {
        if (response instanceof HttpResponse) {
          this.handleRedirectResponse(response);
        }
      }),
      tap(() => this.checkLastActivity()),
      catchError((error: HttpErrorResponse) => {
        this.handleServerError(error);
        this.handleUnauthenticatedError(error);
        return throwError(error);
      }),
    );
  }

  private handleRedirectResponse(response: HttpResponse<any>): void {
    const locationFromHeader = response.headers.get('Location');
    if (locationFromHeader) {
      const urlWithoutDomain = locationFromHeader.replace(/.*\/\/[^\/]*/, '');
      this.router.navigateByUrl(urlWithoutDomain);
    }
  }

  private checkLastActivity(): void {
    const currentUser = this.userService.getUser();
    if (currentUser && currentUser.last_activity && moment(currentUser.last_activity).isAfter(moment(), 'day')) {
      this.userService.fetchUser().subscribe();
    }
  }

  private handleServerError(error: HttpErrorResponse): void {
    const hasServerError = isServerError(error);

    if (hasServerError) {
      const serverSideErrorMessage = 'Something went wrong. Please contact support@constrafor.com.';
      this.snackbarService.error(serverSideErrorMessage);
    }
  }

  private handleUnauthenticatedError(error: HttpErrorResponse): void {
    if (
      error.status === 403 &&
      error.error &&
      error.error.detail &&
      error.error.detail.includes('Authentication credentials were not provided.')
    ) {
      this.authService.logout().subscribe(() => {
        this.router.navigate(['auth', 'login'], { queryParams: { redirect: this.router.url } });
      });
      resetHubspotCookiesAndReloadPage();
    }
  }
}
